import Tastet from '../tastet.js';

const STATES = {
  OPEN: 'open',
};

export default class VideoGallery extends Tastet.View {
  // init ------------------------------------------------------------------------------------------------------------
  init($el, data) {
    this.$items = this.$element.find('[data-video-url]');
    this.$modal = this.$body.find('[data-video-modal]');
    this.$player = this.$modal.find('.video-modal__player');
    this.$overlay = this.$modal.find('.video-modal__overlay');
    this.$close = this.$modal.find('.video-modal__close');
    this.$loader = this.$modal.find('.video-modal__loader');
    this.lastFocusedElement = null;
    this.cloudflareSubdomain = this.$element.data('cloudflare-subdomain') || '';
  }

  addEvents() {
    this.on(this.$items, 'click', this.item_onClick);
    this.on(this.$overlay, 'click', this.close_onClick);
    this.on(this.$close, 'click', this.close_onClick);
    this.on(this.$body, 'keydown', this.body_onKeydown);
  }

  // events ----------------------------------------------------------------------------------------------------------
  item_onClick(e) {
    var target = e.currentTarget || e.target.closest('[data-video-url]');
    if (!target) return;

    var videoUrl = target.getAttribute('data-video-url');
    if (videoUrl) {
      this.lastFocusedElement = target;
      this.open(videoUrl);
    }
  }

  close_onClick() {
    this.close();
  }

  body_onKeydown(e) {
    if (!this.isOpen()) return;

    if (e.key === 'Escape') {
      this.close();
    }

    // Focus trap
    if (e.key === 'Tab') {
      var focusableElements = this.$modal.find('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var firstFocusable = focusableElements.first()[0];
      var lastFocusable = focusableElements.last()[0];

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  // methods ---------------------------------------------------------------------------------------------------------
  isOpen() {
    return this.state(STATES.OPEN) === true;
  }

  /**
   * Convert raw video URL to embeddable iframe URL with autoplay
   * Supports: YouTube, Vimeo, Cloudflare Stream
   */
  getEmbedUrl(url) {
    if (!url) return null;

    // Cloudflare Stream ID (32-char hex string)
    if (/^[a-f0-9]{32}$/.test(url) && this.cloudflareSubdomain) {
      return 'https://' + this.cloudflareSubdomain + '.cloudflarestream.com/' + url + '/iframe?autoplay=true';
    }

    // YouTube
    var youtubeMatch =
      url.match(/youtube\.com\/watch\?v=([^&]+)/) ||
      url.match(/youtu\.be\/([^?]+)/) ||
      url.match(/youtube\.com\/embed\/([^?]+)/);
    if (youtubeMatch) {
      return 'https://www.youtube.com/embed/' + youtubeMatch[1] + '?autoplay=1&rel=0';
    }

    // Vimeo
    var vimeoMatch = url.match(/vimeo\.com\/(\d+)/) || url.match(/player\.vimeo\.com\/video\/(\d+)/);
    if (vimeoMatch) {
      return 'https://player.vimeo.com/video/' + vimeoMatch[1] + '?autoplay=1';
    }

    // Cloudflare Stream - various URL formats
    var cloudflareVideoId = null;

    // Pattern: customer-{subdomain}.cloudflarestream.com/{video_id}/iframe
    var cfCustomerMatch = url.match(/customer-[^.]+\.cloudflarestream\.com\/([a-f0-9]{32})/);
    if (cfCustomerMatch) {
      cloudflareVideoId = cfCustomerMatch[1];
    }

    // Pattern: watch.cloudflarestream.com/{video_id}
    if (!cloudflareVideoId) {
      var cfWatchMatch = url.match(/watch\.cloudflarestream\.com\/([a-f0-9]{32})/);
      if (cfWatchMatch) {
        cloudflareVideoId = cfWatchMatch[1];
      }
    }

    // Pattern: iframe.cloudflarestream.com/{video_id}
    if (!cloudflareVideoId) {
      var cfIframeMatch = url.match(/iframe\.cloudflarestream\.com\/([a-f0-9]{32})/);
      if (cfIframeMatch) {
        cloudflareVideoId = cfIframeMatch[1];
      }
    }

    // Generic: cloudflarestream.com/{video_id}
    if (!cloudflareVideoId) {
      var cfGenericMatch = url.match(/cloudflarestream\.com\/([a-f0-9]{32})/);
      if (cfGenericMatch) {
        cloudflareVideoId = cfGenericMatch[1];
      }
    }

    if (cloudflareVideoId && this.cloudflareSubdomain) {
      return (
        'https://' + this.cloudflareSubdomain + '.cloudflarestream.com/' + cloudflareVideoId + '/iframe?autoplay=true'
      );
    }

    // Fallback: if URL already looks like an embed URL, use it directly
    if (url.includes('/embed') || url.includes('/iframe')) {
      var separator = url.includes('?') ? '&' : '?';
      return url + separator + 'autoplay=1';
    }

    return url;
  }

  open(rawUrl) {
    var embedUrl = this.getEmbedUrl(rawUrl);
    if (!embedUrl) return;

    // Show loader
    if (this.$loader.length) {
      this.$loader.show();
    }

    var iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', 'Video player');

    // Hide loader when iframe loads
    iframe.onload = () => {
      if (this.$loader.length) {
        this.$loader.hide();
      }
    };

    this.$player.find('iframe').remove();
    this.$player.append(iframe);

    // Show modal with animation
    this.$modal.addClass('is-open');
    this.$modal.attr('aria-hidden', 'false');
    this.$body.css('overflow', 'hidden');

    // Focus close button for accessibility
    setTimeout(() => {
      this.$close.focus();
    }, 100);

    this.state(STATES.OPEN, true);
    this.trigger('open');
  }

  close() {
    this.$modal.removeClass('is-open');
    this.$modal.attr('aria-hidden', 'true');

    // Delay iframe removal for fade-out animation
    setTimeout(() => {
      this.$player.find('iframe').remove();
      if (this.$loader.length) {
        this.$loader.show();
      }
    }, 300);

    this.$body.css('overflow', '');
    this.state(STATES.OPEN, false);

    // Restore focus to the element that opened the modal
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }

    this.trigger('close');
  }
}
